import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './Billing.css';

interface Invoice {
  id: string;
  advertiser: string;
  advertiserId: number;
  invoiceNumber: string;
  period: string;
  amount: number;
  currency: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  issuedDate: string;
  dueDate: string;
  paidDate?: string;
  conversionCount: number;
  revenue: number;
}

interface Payout {
  id: string;
  affiliate: string;
  affiliateId: number;
  period: string;
  grossAmount: number;
  fees: number;
  netAmount: number;
  currency: string;
  status: 'pending' | 'processing' | 'paid' | 'failed';
  createdDate: string;
  paidDate?: string;
  conversionCount: number;
}

const Billing: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'invoices' | 'payouts'>('invoices');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        const response = await apiService.getBilling({ type: 'both' });
        setInvoices(response.data.invoices);
        setPayouts(response.data.payouts);
      } catch (error: any) {
        console.error('Failed to fetch billing data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.advertiser.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredPayouts = payouts.filter(payout => {
    const matchesSearch = payout.affiliate.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payout.affiliateId.toString().includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || payout.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'sent': case 'processing': return 'warning';
      case 'overdue': case 'failed': return 'danger';
      case 'draft': case 'pending': return 'info';
      default: return 'secondary';
    }
  };

  const calculateTotals = () => {
    const invoiceStats = {
      total: invoices.reduce((sum, inv) => sum + inv.amount, 0),
      paid: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0),
      pending: invoices.filter(inv => inv.status !== 'paid').reduce((sum, inv) => sum + inv.amount, 0)
    };

    const payoutStats = {
      total: payouts.reduce((sum, pay) => sum + pay.netAmount, 0),
      paid: payouts.filter(pay => pay.status === 'paid').reduce((sum, pay) => sum + pay.netAmount, 0),
      pending: payouts.filter(pay => pay.status !== 'paid').reduce((sum, pay) => sum + pay.netAmount, 0)
    };

    return { invoiceStats, payoutStats };
  };

  const { invoiceStats, payoutStats } = calculateTotals();

  return (
    <div className="billing-page">
      <div className="page-header">
        <h1>Billing & Payouts</h1>
      </div>

      {/* Summary Cards */}
      <div className="billing-summary">
        <div className="summary-card revenue">
          <h3>Total Revenue</h3>
          <div className="amount">${invoiceStats.total.toFixed(2)}</div>
          <div className="sub-stats">
            <span>Paid: ${invoiceStats.paid.toFixed(2)}</span>
            <span>Pending: ${invoiceStats.pending.toFixed(2)}</span>
          </div>
        </div>
        <div className="summary-card payouts">
          <h3>Total Payouts</h3>
          <div className="amount">${payoutStats.total.toFixed(2)}</div>
          <div className="sub-stats">
            <span>Paid: ${payoutStats.paid.toFixed(2)}</span>
            <span>Pending: ${payoutStats.pending.toFixed(2)}</span>
          </div>
        </div>
        <div className="summary-card profit">
          <h3>Net Profit</h3>
          <div className="amount">${(invoiceStats.total - payoutStats.total).toFixed(2)}</div>
          <div className="sub-stats">
            <span>Margin: {((invoiceStats.total - payoutStats.total) / invoiceStats.total * 100).toFixed(1)}%</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="billing-tabs">
        <button
          className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Advertiser Invoices
        </button>
        <button
          className={`tab-btn ${activeTab === 'payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('payouts')}
        >
          Affiliate Payouts
        </button>
      </div>

      {/* Filters */}
      <div className="billing-filters">
        <div className="filters-left">
          <select
            className="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            {activeTab === 'invoices' ? (
              <>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </>
            ) : (
              <>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
              </>
            )}
          </select>
          <div className="export-buttons">
            <button className="btn btn-success">CSV</button>
            <button className="btn btn-warning">Excel</button>
            <button className="btn btn-danger">PDF</button>
          </div>
        </div>
        <div className="search-box">
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Content */}
      <div className="billing-content">
        {activeTab === 'invoices' ? (
          <div className="table-container">
            <table className="billing-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Advertiser</th>
                  <th>Period</th>
                  <th>Conversions</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Issued</th>
                  <th>Due Date</th>
                  <th>Paid Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <span className="invoice-number">{invoice.invoiceNumber}</span>
                    </td>
                    <td>{invoice.advertiser}</td>
                    <td>{invoice.period}</td>
                    <td>{invoice.conversionCount.toLocaleString()}</td>
                    <td>
                      <span className="amount">${invoice.amount.toFixed(2)} {invoice.currency}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(invoice.status)}`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td>{invoice.issuedDate}</td>
                    <td>{invoice.dueDate}</td>
                    <td>{invoice.paidDate || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-info btn-sm">View</button>
                        <button className="btn btn-warning btn-sm">Edit</button>
                        {invoice.status === 'draft' && (
                          <button className="btn btn-success btn-sm">Send</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-container">
            <table className="billing-table">
              <thead>
                <tr>
                  <th>Affiliate ID</th>
                  <th>Affiliate</th>
                  <th>Period</th>
                  <th>Conversions</th>
                  <th>Gross Amount</th>
                  <th>Fees</th>
                  <th>Net Amount</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Paid Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayouts.map((payout) => (
                  <tr key={payout.id}>
                    <td>{payout.affiliateId}</td>
                    <td>{payout.affiliate}</td>
                    <td>{payout.period}</td>
                    <td>{payout.conversionCount.toLocaleString()}</td>
                    <td>
                      <span className="amount">${payout.grossAmount.toFixed(2)}</span>
                    </td>
                    <td>
                      <span className="fees">${payout.fees.toFixed(2)}</span>
                    </td>
                    <td>
                      <span className="net-amount">${payout.netAmount.toFixed(2)} {payout.currency}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(payout.status)}`}>
                        {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                      </span>
                    </td>
                    <td>{payout.createdDate}</td>
                    <td>{payout.paidDate || '-'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-info btn-sm">View</button>
                        {payout.status === 'pending' && (
                          <button className="btn btn-success btn-sm">Process</button>
                        )}
                        {payout.status === 'failed' && (
                          <button className="btn btn-warning btn-sm">Retry</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {activeTab === 'invoices' ? filteredInvoices.length : filteredPayouts.length} entries
        </div>
        <div className="pagination">
          <button className="page-btn">Previous</button>
          <button className="page-btn active">1</button>
          <button className="page-btn">2</button>
          <button className="page-btn">Next</button>
        </div>
      </div>
    </div>
  );
};

export default Billing;